<script>
  import Header from "lib/components/Header.svelte";
  import { Loading } from "carbon-components-svelte";
  import { Tooltip, SectionWrapper } from "paxray-components-svelte";

  export let header = [];
  export let rows = [];
  export let loading = true;
  export let to = "";
  export let from = "";
  export let disableFinalField = false;
  export let marginBottom = "0vh";
  export let sortable = false;
  export let sortColumn = -1;
  export let sortDirection = "asc";
  export let summaryLastRow = false;
  export let nonSortableColumns = [];
  export let variant = "default";
  export let columnWidths = [];
  export let highlightCells = new Set();
  export let warningCells = new Set();
  export let cellTooltips = new Map();
  export let horizontalScroll = false;
  // The pages using this table render their own header.
  export let showHeader = false;
  /** Optional: je Spalte ein kurzer Erklaertext, als [i] im Kopf. */
  export let headerTooltips = [];

  let highlightFrom = -1;
  let highlightTo = -1;

  function setHighlight(i, j) {
    if (i === rows.length - 1 || j === header.length - 1) return;
    if (disableFinalField && !(j < rows[0].length - 1 && i < header.length - 2))
      return;
    highlightFrom = j;
    highlightTo = i;
  }

  function handleHeaderClick(i, head) {
    if (i === header.length - 1 && disableFinalField) return;
    if (sortable && !nonSortableColumns.includes(i)) {
      if (sortColumn === i) {
        sortDirection = sortDirection === "asc" ? "desc" : "asc";
      } else {
        sortColumn = i;
        sortDirection = "asc";
      }
    }
    to = head;
  }

  function displayCell(cell) {
    return isEmptyCell(cell) ? "-" : cell;
  }

  function isEmptyCell(cell) {
    return cell === "" || cell === null || cell === undefined || cell === 0;
  }

  function getCellTooltip(rowIndex, columnIndex) {
    return cellTooltips instanceof Map
      ? cellTooltips.get(`${rowIndex}:${columnIndex}`)
      : cellTooltips?.[`${rowIndex}:${columnIndex}`];
  }
</script>

{#if showHeader}
  <Header section="KPI" breadcrumb="Matrix" updatePageTitle={false}></Header>
{/if}

{#if loading}
  <div class="loading_wrapper">
    <Loading withOverlay={false} />
  </div>
{:else}
  <div
    class="table-container"
    class:modern={variant === "modern"}
    class:horizontal_scroll={horizontalScroll}
    style="--margin-bottom: {marginBottom}"
  >
    <table style="margin-top: 20px; width: 100%;" class="grid-table">
      {#if columnWidths.length > 0}
        <colgroup>
          {#each header as _, i}
            <col style={columnWidths[i] ? `width: ${columnWidths[i]}` : ""} />
          {/each}
        </colgroup>
      {/if}
      <thead>
        <tr>
          {#each header as head, i}
            <th
              class="header {i === 0 ? 'bold' : ''}"
              class:sortable={sortable && !nonSortableColumns.includes(i)}
              class:highlight={highlightFrom === i}
              class:notHover={(i === header.length - 1 && disableFinalField) ||
                nonSortableColumns.includes(i)}
              on:mousedown={() => handleHeaderClick(i, head)}
              >{head}
              {#if headerTooltips[i]}
                <span class="header_hint" on:mousedown|stopPropagation>
                  <Tooltip text={headerTooltips[i]} direction="bottom" />
                </span>
              {/if}
              {#if sortable && sortColumn === i && !nonSortableColumns.includes(i)}
                <img
                  class="sort_marker"
                  src="/img/assistant/icons/chevron_{sortDirection === 'asc'
                    ? 'up'
                    : 'down'}.svg"
                  alt=""
                />
              {/if}</th
            >
          {/each}
        </tr>
      </thead>
      <tbody>
        {#each rows as row, i (`${i}:${row[0]}`)}
          <tr>
            {#each row as cell, j (`${j}:${header[j]}`)}
              {#if j === 0}
                <th
                  class="app_name left"
                  class:summary_row={summaryLastRow && i === rows.length - 1}
                  class:highlight={highlightTo === i}
                  class:selected={from === cell}
                  class:notHover={i === rows.length - 1 && disableFinalField}
                  on:mousedown={() => {
                    if (i === rows.length - 1 && disableFinalField) return;
                    from = cell;
                  }}
                >
                  {cell}
                </th>
              {:else}
                <td
                  class="value {isEmptyCell(cell)
                    ? 'empty-cell'
                    : i === rows.length - 1 || j === header.length - 1
                      ? 'cell-total'
                      : 'cell-content'}"
                  class:summary_row={summaryLastRow && i === rows.length - 1}
                  class:warning_value={warningCells.has(`${i}:${j}`)}
                  class:high_value={highlightCells.has(`${i}:${j}`)}
                  on:mouseenter={() => {
                    if (i !== rows.length - 1 && j !== header.length - 1) {
                      setHighlight(i, j);
                    }
                  }}
                  on:mouseleave={() => {
                    setHighlight(-1, -1);
                  }}
                  on:mousedown={() => {
                    if (
                      cell?.type === "link" ||
                      isEmptyCell(cell) ||
                      i === rows.length - 1 ||
                      j === header.length - 1
                    )
                      return;
                    from = row[0];
                    to = header[j];
                  }}
                >
                  <div class="cell">
                    {#if cell?.type === "link"}
                      <a
                        class="action_link"
                        href={cell.href}
                        title={cell.title}
                        aria-label={cell.title}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <img src={cell.icon} alt="" />
                      </a>
                    {:else}
                      {displayCell(cell)}
                      {#if !isEmptyCell(cell) && i !== rows.length - 1 && j !== header.length - 1 && getCellTooltip(i, j)?.lines?.length}
                        <span
                          class="cell-tooltip"
                          class:cell-tooltip-up={i >= rows.length - 2}
                        >
                          <span class="cell-tooltip-title"
                            >{getCellTooltip(i, j).title}</span
                          >
                          {#each getCellTooltip(i, j).lines as line}
                            <span>{line}</span>
                          {/each}
                        </span>
                      {/if}
                    {/if}
                  </div>
                </td>
              {/if}
            {/each}
          </tr>
        {/each}
      </tbody>
    </table>
  </div>
{/if}

<style>
  .header_hint {
    display: inline-flex;
    align-items: center;
    margin-left: 0.25rem;
    vertical-align: middle;
  }

  .table-container {
    height: calc(85vh - var(--margin-bottom));
    overflow-y: visible;
    top: 0;
    max-width: 100%;
    overflow-x: auto;
  }

  .grid-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 5px;
    font-family: Arial, sans-serif;
    color: #ffffff;
    background-color: #1c1c20 !important;
    border: 8px solid #1c1c20 !important;
    border-radius: 0.5rem;
  }

  .grid-table th,
  .grid-table thead,
  .grid-table tbody {
    text-align: center;
  }

  .grid-table .header.bold {
    color: white;
    font-weight: bold;
    height: 100%;
  }

  .grid-table th.header {
    position: sticky !important;
    top: 0;
    z-index: 100;
    padding: 0.5rem 0.3rem;
  }

  .grid-table th.header.bold {
    position: sticky;
    top: 0;
    left: 0;
    z-index: 102;
    padding: 0.5rem 0.3rem;
  }

  .grid-table th.app_name,
  .grid-table td:first-child {
    position: sticky;
    left: 0;
    z-index: 101;
    background-color: #2f4858;
  }

  .grid-table th.left {
    background-color: #2f4858;
    text-align: center;
    vertical-align: middle;
    height: 100%;
    font-weight: normal;
    padding: 0.5rem;
  }

  .grid-table th.left.selected {
    background-color: #76bfea;
    color: #282828;
    outline: 2px solid #f2f2f2;
    outline-offset: -2px;
  }

  .header,
  .app_name,
  .notHover {
    background-color: #2f4858;
    font-weight: normal;
  }

  .grid-table td {
    background-color: #3e6373 !important;
    height: auto;
    vertical-align: middle;
  }

  .grid-table td .value {
    background-color: #2f4858 !important;
    text-align: center;
    height: 100%;
    font-weight: normal;
    padding: 0.5rem;
    z-index: 105;
    justify-items: center;
  }

  .grid-table td .cell-content,
  .grid-table td .cell-total {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    background-color: #3e6373 !important;
    color: white;
  }

  .cell {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    height: 100%;
    width: 100%;
  }

  .cell-tooltip {
    display: none;
    position: absolute;
    left: 50%;
    top: calc(100% + 0.3rem);
    z-index: 6000;
    width: max-content;
    min-width: 10rem;
    max-width: 24rem;
    padding: 0.45rem 0.55rem;
    transform: translateX(-50%);
    background: #1c1c20;
    color: #fff;
    box-shadow: 0 0.5rem 1.2rem rgba(0, 0, 0, 0.2);
    font-size: 11px;
    font-weight: 500;
    line-height: 1.3;
    pointer-events: none;
    text-align: left;
    white-space: normal;
  }

  .cell-tooltip.cell-tooltip-up {
    top: auto;
    bottom: calc(100% + 0.3rem);
  }

  .cell-tooltip span {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .cell-tooltip .cell-tooltip-title {
    margin-bottom: 0.25rem;
    font-size: 10px;
    font-weight: 800;
    opacity: 0.9;
  }

  .cell:hover .cell-tooltip,
  .cell:focus-within .cell-tooltip {
    display: block;
  }

  .grid-table td.value {
    position: relative;
  }

  .grid-table td.value:hover {
    z-index: 500;
  }

  .grid-table td.value.cell-content:hover {
    background-color: #76bbea !important;
    cursor: pointer;
  }

  /* .grid-table .highlight {
    background-color: #76bfea !important;
    color: #282828;
    font-weight: normal !important;
  } */

  .loading_wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 80vh;
    width: 100%;
  }

  .grid-table td.empty-cell {
    background-color: #2f4858 !important;
    cursor: not-allowed;
  }

  .grid-table td.high_value {
    background-color: #e6a7ad !important;
    color: var(--pax-black) !important;
    font-weight: 700 !important;
  }

  .grid-table td.value.cell-content.high_value:hover {
    background-color: #e6a7ad !important;
    color: var(--pax-black) !important;
  }

  .grid-table td.high_value .cell {
    color: var(--pax-black) !important;
    font-weight: 700 !important;
  }

  .grid-table td.warning_value {
    background-color: #e5e8a8 !important;
    color: var(--pax-black) !important;
    font-weight: 700 !important;
  }

  .grid-table td.value.cell-content.warning_value:hover {
    background-color: #e5e8a8 !important;
    color: var(--pax-black) !important;
  }

  .grid-table td.warning_value.high_value,
  .grid-table td.value.cell-content.warning_value.high_value:hover {
    background-color: #e6a7ad !important;
    color: var(--pax-black) !important;
    font-weight: 700 !important;
  }

  .grid-table td.warning_value .cell {
    color: var(--pax-black) !important;
    font-weight: 700 !important;
  }

  th.highlight,
  th:hover {
    background-color: #76bfea !important;
    color: #282828;
    font-weight: normal !important;
    cursor: pointer;
  }

  th.sortable {
    cursor: pointer;
  }

  th.sortable.notHover {
    cursor: default;
  }

  .sort_marker {
    display: inline-block;
    width: 0.65rem;
    height: 0.4rem;
    margin-left: 0.25rem;
    vertical-align: middle;
  }

  .notHover:hover {
    background-color: #2f4858 !important;
    color: white !important;
    cursor: default !important;
  }

  .grid-table th.summary_row,
  .grid-table td.summary_row {
    background-color: var(--pax-black) !important;
    color: var(--pax-white);
    font-weight: 700;
    cursor: not-allowed !important;
  }

  .grid-table th.summary_row:hover,
  .grid-table td.summary_row:hover {
    background-color: var(--pax-black) !important;
    color: var(--pax-white) !important;
    cursor: not-allowed !important;
  }

  .action_link {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.75rem;
    height: 1.75rem;
    border-radius: 0.375rem;
    background-color: var(--pax-black);
  }

  .action_link img {
    width: 1rem;
    height: 1rem;
    filter: brightness(0) invert(1);
  }

  .table-container.modern {
    height: calc(100% - var(--margin-bottom));
    padding: 0;
    background-color: var(--pax-white);
    border: none;
    border-radius: 0.5rem;
    box-sizing: border-box;
    overflow: auto;
  }

  .table-container.modern .grid-table {
    table-layout: fixed;
    margin-top: 0 !important;
    border-spacing: 0;
    font-family: Inter, sans-serif;
    color: var(--pax-black);
    background-color: var(--pax-white) !important;
    border: none !important;
    border-radius: 0.5rem;
    overflow: hidden;
  }

  .table-container.modern .grid-table th.header {
    background-color: var(--pax-black);
    color: var(--pax-white);
    border-bottom: 1px solid #e0e0e0;
    font-weight: 600;
    padding: 0.5rem 0.65rem;
    line-height: 1.2;
    text-align: right;
    box-sizing: border-box;
  }

  .table-container.modern .grid-table th.header.bold,
  .table-container.modern .grid-table th.app_name {
    background-color: var(--pax-black);
    color: var(--pax-white);
  }

  .table-container.modern .grid-table th.left {
    background-color: var(--pax-black);
    color: var(--pax-white);
    border-bottom: 1px solid #ededed;
    border-right: 1px solid #ededed;
    font-weight: 500;
    padding: 0.45rem 0.65rem;
    text-align: left;
    box-sizing: border-box;
  }

  .table-container.modern .grid-table th.header.bold {
    text-align: left;
  }

  .table-container.modern .grid-table th.header:last-child,
  .table-container.modern .grid-table td:last-child {
    padding-right: 1rem;
  }

  .table-container.modern .grid-table td {
    background-color: var(--pax-white) !important;
    border-bottom: 1px solid #ededed;
    color: var(--pax-black);
    padding: 0.45rem 0.65rem;
    box-sizing: border-box;
  }

  .table-container.modern .grid-table tbody tr:nth-child(even) td {
    background-color: #f7f7f7 !important;
  }

  .table-container.modern .grid-table tbody tr td.high_value,
  .table-container.modern .grid-table tbody tr:nth-child(even) td.high_value,
  .table-container.modern
    .grid-table
    tbody
    tr:hover
    td.high_value:not(.summary_row),
  .table-container.modern .grid-table td.high_value:hover {
    background-color: #e6a7ad !important;
    color: var(--pax-black) !important;
    font-weight: 700 !important;
  }

  .table-container.modern .grid-table td.high_value .cell {
    color: var(--pax-black) !important;
    font-weight: 700 !important;
  }

  .table-container.modern .grid-table tbody tr td.warning_value,
  .table-container.modern .grid-table tbody tr:nth-child(even) td.warning_value,
  .table-container.modern
    .grid-table
    tbody
    tr:hover
    td.warning_value:not(.summary_row),
  .table-container.modern .grid-table td.warning_value:hover {
    background-color: #e5e8a8 !important;
    color: var(--pax-black) !important;
    font-weight: 700 !important;
  }

  .table-container.modern .grid-table tbody tr td.warning_value.high_value,
  .table-container.modern
    .grid-table
    tbody
    tr:nth-child(even)
    td.warning_value.high_value,
  .table-container.modern
    .grid-table
    tbody
    tr:hover
    td.warning_value.high_value:not(.summary_row),
  .table-container.modern .grid-table td.warning_value.high_value:hover {
    background-color: #e6a7ad !important;
    color: var(--pax-black) !important;
    font-weight: 700 !important;
  }

  .table-container.modern .grid-table tbody tr:hover td:not(.summary_row) {
    background-color: #f4f4f4 !important;
    box-shadow:
      inset 0 1px 0 var(--pax-black),
      inset 0 -1px 0 var(--pax-black);
  }

  .table-container.modern
    .grid-table
    tbody
    tr:hover
    td:not(.summary_row):first-child {
    box-shadow:
      inset 1px 0 0 var(--pax-black),
      inset 0 1px 0 var(--pax-black),
      inset 0 -1px 0 var(--pax-black);
  }

  .table-container.modern
    .grid-table
    tbody
    tr:hover
    td:not(.summary_row):last-child {
    box-shadow:
      inset -1px 0 0 var(--pax-black),
      inset 0 1px 0 var(--pax-black),
      inset 0 -1px 0 var(--pax-black);
  }

  .table-container.modern .cell {
    min-height: 2.4rem;
    color: var(--pax-black);
    font-weight: 500;
    justify-content: flex-end;
    text-align: right;
  }

  .table-container.modern td .cell {
    font-family: "Courier New", monospace;
  }

  .table-container.modern th.left .cell {
    justify-content: flex-start;
    text-align: left;
  }

  .table-container.modern .grid-table td.empty-cell {
    background-color: #fafafa !important;
    cursor: not-allowed;
  }

  .table-container.modern .grid-table tbody tr:nth-child(even) td.empty-cell {
    background-color: #f2f2f2 !important;
  }

  .table-container.modern .grid-table td.value.cell-content:hover {
    background-color: #eeeeee !important;
    cursor: pointer;
  }

  .table-container.modern .grid-table th.summary_row,
  .table-container.modern .grid-table td.summary_row,
  .table-container.modern .grid-table tbody tr:nth-child(even) td.summary_row {
    background-color: var(--pax-black) !important;
    color: var(--pax-white);
    cursor: not-allowed !important;
  }

  .table-container.modern .grid-table th.summary_row:hover,
  .table-container.modern .grid-table td.summary_row:hover {
    background-color: var(--pax-black) !important;
    color: var(--pax-white) !important;
    cursor: not-allowed !important;
  }

  .table-container.modern .grid-table .summary_row .cell {
    color: var(--pax-white);
  }

  .table-container.modern th.highlight,
  .table-container.modern th:hover {
    background-color: #34343a !important;
    color: var(--pax-white);
  }

  .table-container.modern th.header:hover,
  .table-container.modern th.header.highlight {
    font-weight: 600 !important;
  }

  .table-container.modern th.left:hover,
  .table-container.modern th.left.highlight {
    font-weight: 500 !important;
  }

  .table-container.modern .notHover:hover {
    background-color: var(--pax-black) !important;
    color: var(--pax-white) !important;
  }

  .table-container.modern .sort_marker {
    width: 0.7rem;
    height: 0.4rem;
  }

  .table-container.modern .action_link {
    background-color: var(--pax-black);
  }

  .table-container.modern.horizontal_scroll {
    max-width: 100%;
    overflow-x: auto;
  }

  .table-container.modern.horizontal_scroll .grid-table {
    table-layout: auto;
    width: max-content !important;
    min-width: 100%;
    overflow: visible;
  }

  .table-container.modern.horizontal_scroll .grid-table th,
  .table-container.modern.horizontal_scroll .grid-table td {
    min-width: 8rem;
    white-space: nowrap;
  }

  .table-container.modern.horizontal_scroll .grid-table th:first-child,
  .table-container.modern.horizontal_scroll .grid-table td:first-child {
    min-width: 14rem;
    max-width: 22rem;
    white-space: normal;
  }
</style>
